/**
 * Weekly (temporal page) KPI engine.
 *
 * Client-side port of the legacy engine's `calculate_kpi_changes` with
 * inventory metrics enabled (kpi_logic.py): the per-week simple cascade from
 * the edited week forward, then the temporally coupled passes — EOP/BOP
 * roll-forward opening on the edited week's own BOP, derived stock AUCs, ROST
 * (rate of stock turnover, forward 52-week window) and the always
 * recalculated target-stock / open-to-buy pass (forward FPOS window sums).
 *
 * Parity with the Python engine is pinned by weeklyKpiEngine.parity.test.ts
 * against calc-vectors/weekly-cascade.json.
 */

import {
  type MetricValues,
  recalcDerivedMetrics,
  safeDivide
} from "../summary/editPropagation";
import { applyKpiChange, hasKpiPipeline } from "../summary/kpiCalculations";
import {
  markdownFlowSign,
  SALES_KPI_DERIVATIONS
} from "../summary/salesKpiDerivations";

const NIS3 = "NIS3";
const NS3_COGS = "NS3_COGS";
const INTAKE_UNITS_TOTAL = "INTAKE_UNITS_TOTAL";
const INTAKE_AT_COST_TOTAL = "INTAKE_AT_COST_TOTAL";
const BOP_UNITS = "BEGINNING_OF_PERIOD_UNITS";
export const EOP_UNITS = "END_OF_PERIOD_UNITS";
const BOP_AT_COST = "BEGINNING_OF_PERIOD_AT_COST";
const EOP_AT_COST = "END_OF_PERIOD_AT_COST";
const BOP_AUC = "BEGINNING_OF_PERIOD_AUC";
const EOP_AUC = "END_OF_PERIOD_AUC";
export const FPOS = "FORWARD_PERIOD_OF_SALES";
export const FPOS_AT_COST = "FORWARD_PERIOD_OF_SALES_AT_COST";
const TARGET_STOCK_UNITS = "TARGET_STOCK_UNITS_TOTAL";
const TARGET_STOCK_AT_COST = "TARGET_STOCK_AT_COST";
const TARGET_STOCK_AUC = "TARGET_STOCK_AUC";
const OPEN_TO_BUY_UNITS = "OPEN_TO_BUY_UNITS";
const OPEN_TO_BUY_AT_COST = "OPEN_TO_BUY_AT_COST";
const OPEN_TO_BUY_AUC = "OPEN_TO_BUY_AUC";
const PLANNED_OPEN_TO_BUY_UNITS = "PLANNED_OPEN_TO_BUY_UNITS";
const PLANNED_OPEN_TO_BUY_AT_COST = "PLANNED_OPEN_TO_BUY_AT_COST";
const PLANNED_OPEN_TO_BUY_AUC = "PLANNED_OPEN_TO_BUY_AUC";
const ROST_UNITS = "RATE_OF_STOCK_TURNOVER_UNITS";
const ROST_AT_COST = "RATE_OF_STOCK_TURNOVER_AT_COST";
const NEW_MD_UNITS = "NEW_MD_UNITS";
const NEW_MD_COST = "NEW_MD_COST";

/** Float-precision epsilon used by the FPOS inverse scan (EPSILON_TS). */
const EPSILON_TS = 1e-3;

interface StockFlow {
  bop: string;
  eop: string;
  ns: string;
  intake: string;
  fpos: string;
  ts: string;
  otb: string;
  /** Planned OTB: the running-max increment of `otb`. */
  potb: string;
  rost: string;
  /** NEW-markdown leaf whose (signed) value corrects EOP as units move FP->MD. */
  markdownLeaf: string;
  /** OTB units are truncated to whole units; at-cost is not. */
  truncateOtb: boolean;
}

const UNIT_FLOW: StockFlow = {
  bop: BOP_UNITS,
  eop: EOP_UNITS,
  ns: NIS3,
  intake: INTAKE_UNITS_TOTAL,
  fpos: FPOS,
  ts: TARGET_STOCK_UNITS,
  otb: OPEN_TO_BUY_UNITS,
  potb: PLANNED_OPEN_TO_BUY_UNITS,
  rost: ROST_UNITS,
  markdownLeaf: NEW_MD_UNITS,
  truncateOtb: true
};

const AT_COST_FLOW: StockFlow = {
  bop: BOP_AT_COST,
  eop: EOP_AT_COST,
  ns: NS3_COGS,
  intake: INTAKE_AT_COST_TOTAL,
  fpos: FPOS_AT_COST,
  ts: TARGET_STOCK_AT_COST,
  otb: OPEN_TO_BUY_AT_COST,
  potb: PLANNED_OPEN_TO_BUY_AT_COST,
  rost: ROST_AT_COST,
  markdownLeaf: NEW_MD_COST,
  truncateOtb: false
};

const FLOWS = [UNIT_FLOW, AT_COST_FLOW];

/** Flow inputs the stock passes read every week: bop seeds the roll at the
 *  edited week and feeds fillRost on the weeks before it (rollForward only
 *  writes from the edited week), eop feeds the OTB pass, ns/intake feed the
 *  roll and fpos the target-stock scan. Missing keys coerce to 0 here, so the adapter's
 *  completeness guard must cover exactly this list. */
export const WEEKLY_FLOW_INPUT_METRICS = [
  ...new Set(
    FLOWS.flatMap(function inputs(flow) {
      return [flow.bop, flow.eop, flow.ns, flow.intake, flow.fpos];
    })
  )
];

/** Temporally coupled trigger pipelines (COUPLED_KPI_PIPELINES). */
const COUPLED_KPI_PIPELINES: Record<string, string[]> = {
  // TS AUC is the held price coupling the flows.
  [FPOS]: [TARGET_STOCK_UNITS, TARGET_STOCK_AT_COST, FPOS_AT_COST],
  [FPOS_AT_COST]: [TARGET_STOCK_AT_COST, TARGET_STOCK_UNITS, FPOS],
  [TARGET_STOCK_UNITS]: [TARGET_STOCK_AT_COST, FPOS, FPOS_AT_COST],
  [TARGET_STOCK_AT_COST]: [TARGET_STOCK_UNITS, FPOS, FPOS_AT_COST],
  // A price entry holds TS Units and moves TS @Cost.
  [TARGET_STOCK_AUC]: [TARGET_STOCK_AT_COST, FPOS_AT_COST]
};

/** A batch entering both flows keeps every typed value (OWN_FLOW_PIPELINES). */
const OWN_FLOW_PIPELINES: Record<string, string[]> = {
  [FPOS]: [TARGET_STOCK_UNITS],
  [FPOS_AT_COST]: [TARGET_STOCK_AT_COST],
  [TARGET_STOCK_UNITS]: [FPOS],
  [TARGET_STOCK_AT_COST]: [FPOS_AT_COST]
};
const UNITS_SIDE = new Set([FPOS, TARGET_STOCK_UNITS]);
const COST_SIDE = new Set([FPOS_AT_COST, TARGET_STOCK_AT_COST]);

const TARGET_STOCK_TRIPLET = [
  TARGET_STOCK_UNITS,
  TARGET_STOCK_AT_COST,
  TARGET_STOCK_AUC
];

/** Extra frame columns the coupled pipelines' derived branches read per
 *  trigger, beyond WEEKLY_FLOW_INPUT_METRICS. The trigger row is only
 *  guaranteed in the edited fiscal year, so a trigger whose column is read
 *  across all weeks (the TS edits' FPOS scan) must list itself here. Same
 *  missing-key-to-0 hazard. */
export const WEEKLY_COUPLED_INPUT_METRICS: Record<string, string[]> = {
  [FPOS]: TARGET_STOCK_TRIPLET,
  [FPOS_AT_COST]: TARGET_STOCK_TRIPLET,
  [TARGET_STOCK_UNITS]: TARGET_STOCK_TRIPLET,
  [TARGET_STOCK_AT_COST]: TARGET_STOCK_TRIPLET,
  [TARGET_STOCK_AUC]: TARGET_STOCK_TRIPLET
};

function v(week: MetricValues, key: string): number {
  return week[key] ?? 0;
}

function column(weeks: MetricValues[], key: string): number[] {
  return weeks.map(function read(week) {
    return v(week, key);
  });
}

function sumRange(values: number[], from: number, to: number): number {
  let sum = 0;
  for (let j = from; j < to; j++) sum += values[j];
  return sum;
}

/**
 * Forward FPOS-window sum (_calculate_ts_numba): target stock at week i is
 * the sum of the next ceil(fpos) weeks of sales, with the fractional excess
 * of the last window week removed. NaN when fpos is NaN or the window
 * exceeds the horizon — unless `partialWindow`, where it sums the weeks that remain
 * instead, as the cube's restate does (`_calculate_ts_cte`).
 */
function targetStockScan(
  stock: number[],
  fpos: number[],
  partialWindow = false
): number[] {
  const n = stock.length;
  const out = new Array<number>(n).fill(Number.NaN);
  for (let i = 0; i < n; i++) {
    const f = fpos[i];
    if (Number.isNaN(f)) continue;
    // Negative FPOS is deliberately not ported: the numba kernel wraps the
    // negative window index (itself meaningless), and the adapter refuses a
    // negative FPOS. Yield NaN rather than inherit JS out-of-range indexing.
    if (f < 0) continue;
    if (f === 0) {
      out[i] = 0;
      continue;
    }
    const windowSize = Math.ceil(f);
    const endIndex = i + windowSize + 1;
    if (endIndex > n) {
      if (partialWindow) out[i] = sumRange(stock, i + 1, n);
      continue;
    }
    const sum = sumRange(stock, i + 1, endIndex);
    const excess = windowSize - f;
    out[i] = sum - stock[endIndex - 1] * excess;
  }
  return out;
}

/**
 * Target stock restated from FPOS, keeping the stored target where the
 * forward window overruns the horizon and no FPOS exists (_calculate_ts).
 */
function targetStockFromFpos(
  weeks: MetricValues[],
  flow: StockFlow,
  partialWindow = false
): number[] {
  const scanned = targetStockScan(
    column(weeks, flow.ns),
    column(weeks, flow.fpos),
    partialWindow
  );
  return scanned.map(function keepStored(value, i) {
    return Number.isNaN(v(weeks[i], flow.fpos)) ? v(weeks[i], flow.ts) : value;
  });
}

/**
 * Inverse of the FPOS-window sum (_calculate_fpos_numba): how many forward
 * weeks of sales cover the target stock, fractional on the last week.
 */
function fposScan(ts: number[], stock: number[]): number[] {
  const n = ts.length;
  const out = new Array<number>(n).fill(Number.NaN);
  for (let i = 0; i < n; i++) {
    const target = ts[i];
    if (target === 0) {
      out[i] = 0;
      continue;
    }
    if (target < 0 || Number.isNaN(target)) continue;
    let covered = EPSILON_TS;
    let fpos = 0;
    let endIndex = i + 1;
    while (endIndex < n && covered < target) {
      covered += stock[endIndex];
      fpos += 1;
      endIndex += 1;
    }
    if (covered < target) continue;
    if (Math.abs(covered - target) < 1.0) {
      out[i] = fpos;
    } else {
      out[i] = fpos - (covered - EPSILON_TS - target) / stock[endIndex - 1];
    }
  }
  return out;
}

function setColumn(weeks: MetricValues[], key: string, values: number[]): void {
  for (let i = 0; i < weeks.length; i++) {
    weeks[i][key] = values[i];
  }
}

/** TS @Cost = TS AUC × TS Units; a week without a price keeps its TS @Cost (_ts_at_cost_from_price). */
function targetStockCostFromPrice(
  weeks: MetricValues[],
  trigger: string
): number[] {
  return weeks.map(function price(week) {
    const units = week[TARGET_STOCK_UNITS] ?? Number.NaN;
    const auc = week[TARGET_STOCK_AUC] ?? Number.NaN;
    // A TS AUC entry on a week without units has no price to set; for a units
    // entry a zero AUC is no price (units 0 under a cost), so the cost stays.
    const noPrice = trigger === TARGET_STOCK_AUC ? units === 0 : auc === 0;
    const priced = !Number.isNaN(units) && !Number.isNaN(auc) && !noPrice;
    return priced ? auc * units : v(week, TARGET_STOCK_AT_COST);
  });
}

/** TS Units = TS @Cost ÷ TS AUC; a week without a price keeps its TS Units (_ts_units_from_price). */
function targetStockUnitsFromPrice(weeks: MetricValues[]): number[] {
  return weeks.map(function price(week) {
    const cost = week[TARGET_STOCK_AT_COST] ?? Number.NaN;
    const auc = week[TARGET_STOCK_AUC] ?? Number.NaN;
    const priced = !Number.isNaN(cost) && !Number.isNaN(auc) && auc !== 0;
    return priced ? cost / auc : v(week, TARGET_STOCK_UNITS);
  });
}

const COST_FROM_PRICE = new Set([...UNITS_SIDE, TARGET_STOCK_AUC]);

/** calculate_ts_units / calculate_ts_at_cost / calculate_fpos_units /
 *  calculate_fpos_at_cost (KPI_CALCULATIONS). */
function applyCoupledDependent(
  weeks: MetricValues[],
  dependent: string,
  trigger: string
): void {
  if (dependent === TARGET_STOCK_UNITS) {
    setColumn(
      weeks,
      dependent,
      COST_SIDE.has(trigger)
        ? targetStockUnitsFromPrice(weeks)
        : targetStockFromFpos(weeks, UNIT_FLOW)
    );
    return;
  }
  if (dependent === TARGET_STOCK_AT_COST) {
    setColumn(
      weeks,
      dependent,
      COST_FROM_PRICE.has(trigger)
        ? targetStockCostFromPrice(weeks, trigger)
        : targetStockFromFpos(weeks, AT_COST_FLOW)
    );
    return;
  }
  if (dependent === FPOS) {
    setColumn(
      weeks,
      dependent,
      fposScan(column(weeks, UNIT_FLOW.ts), column(weeks, UNIT_FLOW.ns))
    );
    return;
  }
  if (dependent === FPOS_AT_COST) {
    setColumn(
      weeks,
      dependent,
      fposScan(column(weeks, AT_COST_FLOW.ts), column(weeks, AT_COST_FLOW.ns))
    );
  }
}

function rollForward(
  weeks: MetricValues[],
  fromWeek: number,
  markdownSign: number
): void {
  // The roll opens on `fromWeek`'s own BOP, so the stock an intake swap posted
  // there survives an edit. It only touches weeks >= fromWeek.
  // The signed NEW-markdown leaf is folded in (units moving FP->MD) so the
  // shift persists forward, matching the server's calculate_eop_bop_numba.
  for (const flow of FLOWS) {
    let bop = v(weeks[fromWeek], flow.bop);
    for (let i = fromWeek; i < weeks.length; i++) {
      const eop =
        bop -
        v(weeks[i], flow.ns) +
        v(weeks[i], flow.intake) +
        markdownSign * markedDown(weeks[i], flow.markdownLeaf);
      weeks[i][flow.bop] = bop;
      weeks[i][flow.eop] = eop;
      bop = eop;
    }
  }
  fillStockAuc(weeks, fromWeek);
}

/** A week's planned-markdown leaf; blank is nothing marked down, as the server reads it. */
function markedDown(week: MetricValues, leaf: string): number {
  const value = week[leaf];
  return value == null || Number.isNaN(value) ? 0 : value;
}

/** BOP/EOP AUC on the weeks from `fromWeek` (_calculate_derived_stock_kpis). */
function fillStockAuc(weeks: MetricValues[], fromWeek: number): void {
  for (let i = fromWeek; i < weeks.length; i++) {
    weeks[i][BOP_AUC] = safeDivide(
      v(weeks[i], BOP_AT_COST),
      v(weeks[i], BOP_UNITS)
    );
    weeks[i][EOP_AUC] = safeDivide(
      v(weeks[i], EOP_AT_COST),
      v(weeks[i], EOP_UNITS)
    );
  }
}

function fillRost(weeks: MetricValues[]): void {
  // Forward 52-week window: SUM(sales) / AVG(opening stock), min one week.
  // NaN holes are skipped per column (pandas rolling min_periods=1); an
  // all-NaN window yields NaN.
  const n = weeks.length;
  for (const flow of FLOWS) {
    const ns = column(weeks, flow.ns);
    const bop = column(weeks, flow.bop);
    for (let i = 0; i < n; i++) {
      const end = Math.min(i + 52, n);
      let sum = 0;
      let nsCount = 0;
      let bopSum = 0;
      let bopCount = 0;
      for (let j = i; j < end; j++) {
        if (!Number.isNaN(ns[j])) {
          sum += ns[j];
          nsCount += 1;
        }
        if (!Number.isNaN(bop[j])) {
          bopSum += bop[j];
          bopCount += 1;
        }
      }
      weeks[i][flow.rost] = safeDivide(
        nsCount > 0 ? sum : Number.NaN,
        bopCount > 0 ? bopSum / bopCount : Number.NaN
      );
    }
  }
}

function fillTsOtb(weeks: MetricValues[], partialWindow = false): void {
  for (const flow of FLOWS) {
    setColumn(weeks, flow.ts, targetStockFromFpos(weeks, flow, partialWindow));
  }
  for (const week of weeks) {
    week[TARGET_STOCK_AUC] = safeDivide(
      v(week, TARGET_STOCK_AT_COST),
      v(week, TARGET_STOCK_UNITS)
    );
  }
  fillOtb(weeks);
}

/** OTB (target stock less closing stock) on every week (fill_in_otb). */
function fillOtb(weeks: MetricValues[]): void {
  for (const week of weeks) {
    for (const flow of FLOWS) {
      const otb = v(week, flow.ts) - v(week, flow.eop);
      week[flow.otb] =
        flow.truncateOtb && !Number.isNaN(otb) ? Math.trunc(otb) : otb;
    }
    week[OPEN_TO_BUY_AUC] = safeDivide(
      v(week, OPEN_TO_BUY_AT_COST),
      v(week, OPEN_TO_BUY_UNITS)
    );
  }
}

/**
 * Planned OTB: the per-week increment of the running-max OTB fill. The
 * derivation and the NaN rule live in the server's `cascade.cum_otb`.
 *
 * `fromIndex` is the scan origin — the plan's forecast boundary, served as
 * `forecast_week_from`. Weeks before it stay NaN.
 *
 * Pinned against the Python scan by calc-vectors/potb-scan.json.
 */
export function potbFromOtb(otb: number[], fromIndex: number): number[] {
  const out = new Array<number>(otb.length).fill(Number.NaN);
  let cum = 0;
  for (let i = fromIndex; i < otb.length; i++) {
    if (otb[i] == null || Number.isNaN(otb[i])) continue;
    const next = Math.max(cum, otb[i], 0);
    out[i] = next - cum;
    cum = next;
  }
  return out;
}

/** Applies {@link potbFromOtb} per flow and derives the POTB AUC. */
function fillPotb(weeks: MetricValues[], fromIndex: number): void {
  for (const flow of FLOWS) {
    setColumn(
      weeks,
      flow.potb,
      potbFromOtb(column(weeks, flow.otb), fromIndex)
    );
  }
  for (const week of weeks) {
    week[PLANNED_OPEN_TO_BUY_AUC] = safeDivide(
      v(week, PLANNED_OPEN_TO_BUY_AT_COST),
      v(week, PLANNED_OPEN_TO_BUY_UNITS)
    );
  }
}

/** True if the weekly engine can process this trigger (simple or coupled). */
export function isWeeklyTrigger(trigger: string): boolean {
  return hasKpiPipeline(trigger) || trigger in COUPLED_KPI_PIPELINES;
}

export interface WeeklyKpiChanges {
  /** The full weekly frame with every edit already written in; index 0 may be a history week. */
  weeks: MetricValues[];
  /** The edited metrics, in edit order: the server runs their pipelines in this order. */
  triggers: readonly string[];
  /** The first edited week; the cascade and the stock roll start there. */
  fromIndex: number;
  /**
   * Whether the frame carries inventory metrics. When false, only the
   * simple cascade runs (mirrors force_no_inventory: the coupled pipelines
   * and the roll/ROST/TS-OTB postprocessing are skipped).
   */
  inventory?: boolean;
  /**
   * Selected markdown_status context (the applied filter). Drives the sign of
   * the planned-markdown EOP correction; omitted / not a single bucket ⇒ none.
   */
  markdownStatus?: string[] | null;
  /**
   * Index of the first forecast week — POTB's scan origin, a property of the
   * frame rather than of the edit. Omitted ⇒ the POTB pass is skipped and the
   * frame's existing values are left alone, because a scan from the wrong
   * origin would disagree with the server.
   */
  potbFromIndex?: number;
}

/**
 * Recalculates a frame after one or more edits, mirroring
 * calculate_kpi_changes with inventory metrics enabled: every simple pipeline
 * in trigger order, then every coupled one, then one stock roll.
 */
export function applyWeeklyKpiChanges(
  change: WeeklyKpiChanges
): MetricValues[] {
  const { triggers, fromIndex, inventory = true } = change;
  const markdownSign = markdownFlowSign(change.markdownStatus);
  const weeks = change.weeks.map(function clone(week) {
    return { ...week };
  });

  for (const trigger of triggers.filter(hasKpiPipeline)) {
    for (let i = fromIndex; i < weeks.length; i++) {
      applyKpiChange(weeks[i], trigger, v(weeks[i], trigger));
    }
  }

  if (!inventory) return weeks;

  const bothFlows =
    triggers.some((t) => UNITS_SIDE.has(t)) &&
    triggers.some((t) => COST_SIDE.has(t));
  for (const trigger of triggers) {
    const pipelines =
      bothFlows && trigger in OWN_FLOW_PIPELINES
        ? OWN_FLOW_PIPELINES
        : COUPLED_KPI_PIPELINES;
    for (const dependent of pipelines[trigger] ?? []) {
      applyCoupledDependent(weeks, dependent, trigger);
    }
  }

  rollForward(weeks, fromIndex, markdownSign);
  fillRost(weeks);
  fillTsOtb(weeks);
  if (change.potbFromIndex !== undefined) {
    fillPotb(weeks, change.potbFromIndex);
  }
  return weeks;
}

export interface TargetStockRestate {
  /** The full weekly frame. */
  weeks: MetricValues[];
  /**
   * The FPOS, in weeks, that every restated week gets where its window fits
   * the frame.
   */
  fpos: number;
  /** Index of the first week to restate. */
  fromIndex: number;
  /** Index of the last week to restate, inclusive. */
  toIndex: number;
  /**
   * Index of the first forecast week — POTB's scan origin, as in
   * {@link WeeklyKpiChanges}. Omitted ⇒ POTB is left alone.
   */
  potbFromIndex?: number;
}

/**
 * Sets FPOS (units and at cost) to `fpos` in `fromIndex..toIndex` and restates
 * target stock and OTB there, as the cube's "Reset Target Stock" does (spec:
 * item modal, "Reset target stock"). Planned OTB is the increment of a running
 * max, so it is restated through the end of the frame.
 */
export function restateTargetStock(change: TargetStockRestate): MetricValues[] {
  const { fpos, fromIndex, toIndex } = change;
  function inRange(i: number): boolean {
    return i >= fromIndex && i <= toIndex;
  }
  const scanned = change.weeks.map(function setFpos(week, i): MetricValues {
    const next: MetricValues = { ...week };
    if (inRange(i)) {
      next[FPOS] = fpos;
      next[FPOS_AT_COST] = fpos;
    }
    return next;
  });
  fillTsOtb(scanned, true);
  // The server derives FPOS back from target stock, so a week whose window is
  // cut short, or whose target stock is blank, takes what a refetch would show.
  const derivedFpos = FLOWS.map(function derive(flow) {
    return fposScan(column(scanned, flow.ts), column(scanned, flow.ns));
  });
  const windowFits = scanned.length - Math.ceil(fpos);
  // Planned OTB must scan the OTB each week returns, so weeks outside the
  // range keep theirs rather than the rescan's.
  const restated = change.weeks.map(function keepOutsideRange(week, i) {
    if (!inRange(i)) return { ...week };
    const next = scanned[i];
    FLOWS.forEach(function deriveFpos(flow, f) {
      if (i >= windowFits || !Number.isFinite(next[flow.ts])) {
        next[flow.fpos] = derivedFpos[f][i];
      }
    });
    return next;
  });
  if (change.potbFromIndex === undefined) return restated;
  const withPotb = restated.map(function clone(week) {
    return { ...week };
  });
  fillPotb(withPotb, change.potbFromIndex);
  return restated.map(function takePotb(week, i) {
    if (i < fromIndex) return week;
    const next = { ...week };
    for (const metric of TARGET_STOCK_RESTATE_TAIL) {
      next[metric] = withPotb[i][metric];
    }
    return next;
  });
}

/** The planned-OTB rows {@link restateTargetStock} rewrites past its range, through the end of the frame. */
export const TARGET_STOCK_RESTATE_TAIL = [
  ...FLOWS.map((flow) => flow.potb),
  PLANNED_OPEN_TO_BUY_AUC
];

/**
 * Per stock flow (units, at cost): the rows {@link restateTargetStock} reads
 * and the rows it rewrites, target stock first.
 */
export const TARGET_STOCK_RESTATE_FLOWS = FLOWS.map(function restateRows(flow) {
  return {
    // FPOS and target stock too: the scan reads them in every week, and a
    // missing row would read as 0, not NaN.
    reads: [flow.ns, flow.eop, flow.fpos, flow.ts],
    writes: [flow.ts, flow.fpos, flow.otb, flow.potb]
  };
});

/** The AUC rows {@link restateTargetStock} rewrites; each reads both flows. */
export const TARGET_STOCK_RESTATE_AUCS = [
  TARGET_STOCK_AUC,
  OPEN_TO_BUY_AUC,
  PLANNED_OPEN_TO_BUY_AUC
];

/** The intake totals and unit costs, as calculate_derived_intake_kpis derives them. */
const INTAKE_DERIVATIONS = SALES_KPI_DERIVATIONS.filter((definition) =>
  definition.name.startsWith("INTAKE_")
);

/**
 * Recalculates a frame after its intake rows were swapped for another source's,
 * mirroring recalc_intake_kpis: the totals re-derived and the stock rolled from
 * `fromIndex`, opening on that week's own BOP (the swapped-in seed), then OTB,
 * BOP/EOP AUC and ROST on every week, and POTB. Target stock is left as it is.
 *
 * `deriveTotals: false` keeps the swapped-in totals (recalc_derived_intake=False):
 * a frame that carries totals without their legs, as the OTB grid does.
 */
export function applyIntakeSwap(
  change: Omit<WeeklyKpiChanges, "triggers" | "inventory"> & {
    deriveTotals?: boolean;
  }
): MetricValues[] {
  const { fromIndex, deriveTotals = true } = change;
  const weeks = change.weeks.map(function clone(week) {
    return { ...week };
  });
  if (deriveTotals) {
    for (let i = fromIndex; i < weeks.length; i++) {
      weeks[i] = recalcDerivedMetrics(weeks[i], INTAKE_DERIVATIONS);
    }
  }
  rollForward(weeks, fromIndex, markdownFlowSign(change.markdownStatus));
  // Every week: a source swaps closed-week stock too, and OTB is TS − EOP there
  // as on load.
  fillOtb(weeks);
  if (change.potbFromIndex !== undefined) {
    fillPotb(weeks, change.potbFromIndex);
  }
  fillStockAuc(weeks, 0);
  fillRost(weeks);
  return weeks;
}
